<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineAPI from '../../../../../../api/pipelineCards';
import { emitter } from 'shared/helpers/mitt';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import ProgressBar from 'dashboard/components-next/progress/ProgressBar.vue';
import { usePipelinePermissions } from '../../../composables/usePipelinePermissions';
import { findPipelineForCard } from '../../../helpers/pipelineStageResolver';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  checklistItems: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['itemUpdated', 'update:checklistItems', 'goToTab']);

const { t } = useI18n();
const store = useStore();
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const pipelines = computed(() => store.getters['pipelines/getPipelines'] || []);
const itemPipeline = computed(() =>
  findPipelineForCard(
    pipelines.value,
    selectedPipeline.value,
    props.item?.pipeline_id
  )
);
const { canEditCard } = usePipelinePermissions(itemPipeline);

const newChecklistItem = ref('');
const hideCompletedItems = ref(false);
const editingChecklistItemId = ref(null);
const editingChecklistItemText = ref('');
const isSavingChecklist = ref(false);
const isConfirmingChecklistDelete = ref(false);
const deleteConfirmationRef = ref(null);

const normalizePositiveId = value => {
  const normalizedValue = String(value ?? '').trim();
  return /^[1-9]\d*$/.test(normalizedValue) ? normalizedValue : '';
};
const currentChecklistContext = () => ({
  accountId: normalizePositiveId(store.getters.getCurrentAccountId),
  cardId: normalizePositiveId(props.item?.id),
});
const checklistContextMatches = context => {
  const currentContext = currentChecklistContext();
  return (
    currentContext.accountId === context.accountId &&
    currentContext.cardId === context.cardId
  );
};
const checklistFingerprint = items =>
  JSON.stringify(
    items.map(item => ({
      id: item?.id,
      text: item?.text,
      completed: item?.completed,
      createdAt: item?.created_at,
    }))
  );

const filteredChecklistItems = computed(() => {
  if (hideCompletedItems.value) {
    return props.checklistItems.filter(i => !i.completed);
  }
  return props.checklistItems;
});

const totalItems = computed(() => props.checklistItems.length);
const completedItems = computed(
  () => props.checklistItems.filter(i => i.completed).length
);

const checklistProgress = computed(() => {
  if (totalItems.value === 0) return 0;
  return (completedItems.value / totalItems.value) * 100;
});

const addChecklistItem = async () => {
  if (
    !canEditCard.value ||
    !newChecklistItem.value.trim() ||
    isSavingChecklist.value
  ) {
    return;
  }
  const updateContext = currentChecklistContext();
  if (!updateContext.accountId || !updateContext.cardId) return;

  try {
    isSavingChecklist.value = true;
    const newItem = {
      id: Date.now(),
      text: newChecklistItem.value.trim(),
      completed: false,
      created_at: new Date().toISOString(),
    };

    const updatedChecklist = [...props.checklistItems, newItem];

    // Send ONLY the delta — backend deep_merges from DB row.
    // Spreading props.item.item_details would resurrect stale values
    // for OTHER keys (priority, notes, etc) modified concurrently.
    const payload = {
      item_details: {
        checklist: updatedChecklist,
      },
    };

    const { data } = await PipelineAPI.updateCard(
      updateContext.cardId,
      payload,
      { accountId: updateContext.accountId }
    );
    if (!checklistContextMatches(updateContext) || !canEditCard.value) return;
    emit('update:checklistItems', data.item_details.checklist);
    emit('itemUpdated');

    newChecklistItem.value = '';

    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.CHECKLIST_ITEM_ADDED'),
      action: { type: 'success' },
    });

    setTimeout(() => {
      if (checklistContextMatches(updateContext)) {
        emit('goToTab', 'checklist');
      }
    }, 100);
  } catch (error) {
    if (checklistContextMatches(updateContext)) {
      emitter.emit('newToastMessage', {
        message: t('PIPELINE.MESSAGES.CHECKLIST_ITEM_ERROR'),
        action: { type: 'error' },
      });
    }
  } finally {
    isSavingChecklist.value = false;
  }
};

const toggleChecklistItem = async item => {
  if (!canEditCard.value || isSavingChecklist.value) return;
  const updateContext = currentChecklistContext();
  if (!updateContext.accountId || !updateContext.cardId) return;
  try {
    isSavingChecklist.value = true;
    const updatedChecklist = props.checklistItems.map(i => ({
      ...i,
      completed: i.id === item.id ? !i.completed : i.completed,
    }));

    // Send ONLY the delta — backend deep_merges from DB row.
    // Spreading props.item.item_details would resurrect stale values
    // for OTHER keys (priority, notes, etc) modified concurrently.
    const payload = {
      item_details: {
        checklist: updatedChecklist,
      },
    };

    const { data } = await PipelineAPI.updateCard(
      updateContext.cardId,
      payload,
      { accountId: updateContext.accountId }
    );
    if (!checklistContextMatches(updateContext) || !canEditCard.value) return;
    emit('update:checklistItems', data.item_details.checklist);

    setTimeout(() => {
      if (checklistContextMatches(updateContext)) {
        emit('goToTab', 'checklist');
      }
    }, 100);
  } catch (error) {
    if (checklistContextMatches(updateContext)) {
      emitter.emit('newToastMessage', {
        message: t('PIPELINE.CHECKLIST_UPDATE_ERROR'),
        action: { type: 'error' },
      });
    }
  } finally {
    isSavingChecklist.value = false;
  }
};

const removeChecklistItem = async itemToRemove => {
  if (
    !canEditCard.value ||
    isSavingChecklist.value ||
    isConfirmingChecklistDelete.value
  ) {
    return;
  }

  const deleteContext = currentChecklistContext();
  if (!deleteContext.accountId || !deleteContext.cardId) return;
  const checklistSnapshot = props.checklistItems.map(item => ({ ...item }));
  const snapshotFingerprint = checklistFingerprint(checklistSnapshot);
  const itemSnapshot = checklistSnapshot.find(
    item => String(item.id) === String(itemToRemove?.id)
  );
  if (!itemSnapshot) return;

  isConfirmingChecklistDelete.value = true;
  let confirmed = false;
  try {
    confirmed = await deleteConfirmationRef.value?.showConfirmation();
  } finally {
    isConfirmingChecklistDelete.value = false;
  }
  if (
    !confirmed ||
    isSavingChecklist.value ||
    !canEditCard.value ||
    !checklistContextMatches(deleteContext) ||
    checklistFingerprint(props.checklistItems) !== snapshotFingerprint
  ) {
    return;
  }

  try {
    isSavingChecklist.value = true;
    const updatedChecklist = checklistSnapshot.filter(
      item => String(item.id) !== String(itemSnapshot.id)
    );

    // Send ONLY the delta — backend deep_merges from DB row.
    // Spreading props.item.item_details would resurrect stale values
    // for OTHER keys (priority, notes, etc) modified concurrently.
    const payload = {
      item_details: {
        checklist: updatedChecklist,
      },
    };

    const { data } = await PipelineAPI.updateCard(
      deleteContext.cardId,
      payload,
      { accountId: deleteContext.accountId }
    );
    if (!checklistContextMatches(deleteContext) || !canEditCard.value) return;
    emit(
      'update:checklistItems',
      data.item_details?.checklist || updatedChecklist
    );

    setTimeout(() => {
      if (checklistContextMatches(deleteContext)) {
        emit('goToTab', 'checklist');
      }
    }, 100);
  } catch (error) {
    if (checklistContextMatches(deleteContext)) {
      emitter.emit('newToastMessage', {
        message: t('PIPELINE.CHECKLIST_UPDATE_ERROR'),
        action: { type: 'error' },
      });
    }
  } finally {
    isSavingChecklist.value = false;
  }
};

const startEditChecklistItem = item => {
  if (!canEditCard.value || isSavingChecklist.value) return;
  editingChecklistItemId.value = item.id;
  editingChecklistItemText.value = item.text;
};

const saveEditedChecklistItem = async () => {
  const nextText = editingChecklistItemText.value.trim();
  if (!canEditCard.value || !nextText || isSavingChecklist.value) return;
  const updateContext = currentChecklistContext();
  if (!updateContext.accountId || !updateContext.cardId) return;
  try {
    isSavingChecklist.value = true;
    const updatedChecklist = props.checklistItems.map(item => {
      if (item.id === editingChecklistItemId.value) {
        return { ...item, text: nextText };
      }
      return item;
    });

    // Send ONLY the delta — backend deep_merges from DB row.
    // Spreading props.item.item_details would resurrect stale values
    // for OTHER keys (priority, notes, etc) modified concurrently.
    const payload = {
      item_details: {
        checklist: updatedChecklist,
      },
    };

    await PipelineAPI.updateCard(updateContext.cardId, payload, {
      accountId: updateContext.accountId,
    });
    if (!checklistContextMatches(updateContext) || !canEditCard.value) return;
    emit('update:checklistItems', updatedChecklist);

    editingChecklistItemId.value = null;
    editingChecklistItemText.value = '';

    setTimeout(() => {
      if (checklistContextMatches(updateContext)) {
        emit('goToTab', 'checklist');
      }
    }, 100);
  } catch (error) {
    if (checklistContextMatches(updateContext)) {
      emitter.emit('newToastMessage', {
        message: t('PIPELINE.CHECKLIST_UPDATE_ERROR'),
        action: { type: 'error' },
      });
    }
  } finally {
    isSavingChecklist.value = false;
  }
};

const cancelEditChecklistItem = () => {
  editingChecklistItemId.value = null;
  editingChecklistItemText.value = '';
};
</script>

<template>
  <div class="space-y-4">
    <!-- Progress Section -->
    <div v-if="totalItems > 0" class="space-y-2 mb-4">
      <div class="flex items-center justify-between">
        <h4 class="text-xs font-medium text-n-slate-11">
          {{ t('PIPELINE.FORM.CHECKLIST.PROGRESS') }}
        </h4>
        <span class="text-xs font-semibold text-n-slate-12">
          {{ `${completedItems}/${totalItems}` }}
        </span>
      </div>

      <ProgressBar
        :value="checklistProgress"
        :label="t('PIPELINE.FORM.CHECKLIST.PROGRESS')"
      />
    </div>

    <!-- Checklist Title -->
    <h3 class="text-heading-3 text-n-slate-12">
      {{ t('PIPELINE.CHECKLIST_LABEL') }}
    </h3>

    <!-- Add new item -->
    <div v-if="canEditCard" class="flex gap-2 mb-4">
      <input
        v-model="newChecklistItem"
        type="text"
        name="new-checklist-item"
        autocomplete="off"
        :aria-label="t('PIPELINE.CHECKLIST_PLACEHOLDER')"
        class="min-h-11 flex-1 px-3 py-2 text-sm border border-n-weak bg-n-solid-3 rounded-lg focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand text-n-slate-12 placeholder-n-slate-9"
        :placeholder="t('PIPELINE.CHECKLIST_PLACEHOLDER')"
        @keyup.enter="addChecklistItem"
      />
      <button
        type="button"
        class="min-h-11 px-4 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50"
        :disabled="!newChecklistItem.trim() || isSavingChecklist"
        @click="addChecklistItem"
      >
        {{ isSavingChecklist ? t('PIPELINE.SAVING') : t('PIPELINE.ADD') }}
      </button>
    </div>

    <label
      v-if="completedItems > 0"
      class="inline-flex min-h-11 cursor-pointer items-center gap-2 text-sm text-n-slate-11"
    >
      <input
        v-model="hideCompletedItems"
        type="checkbox"
        class="size-4 rounded border-n-weak text-n-brand focus-visible:ring-2 focus-visible:ring-n-brand"
      />
      {{ t('PIPELINE.CHECKLIST_HIDE_COMPLETED') }}
    </label>

    <!-- Checklist items -->
    <div class="space-y-2">
      <div
        v-for="checkItem in filteredChecklistItems"
        :key="checkItem.id"
        class="flex items-center gap-3 p-3 bg-n-solid-3 rounded-lg"
      >
        <input
          type="checkbox"
          :checked="checkItem.completed"
          :disabled="!canEditCard || isSavingChecklist"
          :aria-label="
            t('PIPELINE.CHECKLIST_TOGGLE_ITEM', { item: checkItem.text })
          "
          class="w-4 h-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
          @change="toggleChecklistItem(checkItem)"
        />
        <!-- Edit mode -->
        <template v-if="canEditCard && editingChecklistItemId === checkItem.id">
          <input
            v-model="editingChecklistItemText"
            type="text"
            name="edit-checklist-item"
            autocomplete="off"
            :aria-label="t('PIPELINE.EDIT')"
            class="flex-1 px-2 py-1 text-sm border border-n-weak bg-n-solid-2 rounded focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand text-n-slate-12"
            @keyup.enter="saveEditedChecklistItem"
            @keyup.escape="cancelEditChecklistItem"
          />
          <button
            v-if="canEditCard"
            type="button"
            class="min-h-11 min-w-11 rounded-lg hover:bg-n-teal-3 text-n-teal-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50"
            :disabled="!editingChecklistItemText.trim() || isSavingChecklist"
            :aria-label="t('PIPELINE.SAVE')"
            @click="saveEditedChecklistItem"
          >
            <fluent-icon icon="checkmark" size="14" aria-hidden="true" />
          </button>
          <button
            v-if="canEditCard"
            type="button"
            class="min-h-11 min-w-11 rounded-lg hover:bg-n-alpha-1 text-n-slate-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :aria-label="t('PIPELINE.CANCEL')"
            @click="cancelEditChecklistItem"
          >
            <fluent-icon icon="dismiss" size="14" aria-hidden="true" />
          </button>
        </template>
        <!-- View mode -->
        <template v-else>
          <span
            class="flex-1 text-sm"
            :class="
              checkItem.completed
                ? 'text-n-slate-9 line-through'
                : 'text-n-slate-12'
            "
          >
            {{ checkItem.text }}
          </span>
          <button
            v-if="canEditCard"
            type="button"
            class="min-h-11 min-w-11 rounded-lg hover:bg-n-alpha-1 text-n-slate-10 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :disabled="isSavingChecklist || isConfirmingChecklistDelete"
            :aria-label="t('PIPELINE.EDIT')"
            :title="t('PIPELINE.EDIT')"
            @click="startEditChecklistItem(checkItem)"
          >
            <fluent-icon icon="edit" size="14" aria-hidden="true" />
          </button>
          <button
            v-if="canEditCard"
            type="button"
            class="min-h-11 min-w-11 rounded-lg hover:bg-n-ruby-3 text-n-slate-10 hover:text-n-ruby-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50"
            :disabled="isSavingChecklist || isConfirmingChecklistDelete"
            :aria-label="t('PIPELINE.DELETE')"
            :title="t('PIPELINE.DELETE')"
            @click="removeChecklistItem(checkItem)"
          >
            <fluent-icon icon="delete" size="14" aria-hidden="true" />
          </button>
        </template>
      </div>
    </div>

    <ConfirmationModal
      ref="deleteConfirmationRef"
      :title="t('PIPELINE.CHECKLIST_DELETE_TITLE')"
      :description="t('PIPELINE.CHECKLIST_DELETE_CONFIRM')"
      :confirm-label="t('PIPELINE.DELETE')"
      :cancel-label="t('PIPELINE.CANCEL')"
    />
  </div>
</template>
